export interface RequestConfig extends Omit<RequestInit, 'body'> {
  body?: any;
  timeout?: number;
}

export class HttpAnonymousClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor(
    baseURL: string, 
    defaultHeaders: Record<string, string> = {}
  ) {
    this.baseURL = baseURL;
    this.defaultHeaders = defaultHeaders;
  }

  private async buildHeaders(customHeaders?: Record<string, string>): Promise<Record<string, string>> {
    
    return {
      ...this.defaultHeaders,
      ...customHeaders,
    };
  }

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const { body, timeout = 30000, ...fetchConfig } = config;
    
    const headers = await this.buildHeaders(fetchConfig.headers as Record<string, string>);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...fetchConfig,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      if (!response.ok) {
        const error: any = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.body = await response.text();
        throw error;
      }

      if (response.status === 204) {
        console.log(`No content returned for ${endpoint}`);
        return {} as T; // Return empty object for 204 No Content
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  get<T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  post<T>(endpoint: string, body?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  put<T>(endpoint: string, body?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  delete<T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }
}